import { Component, Input, computed, signal } from '@angular/core';
import {
  ButtonsWidget,
  ButtonItem,
  Theme,
  ButtonsStyle,
  ButtonColorSet,
} from '../../models/reader-document';

interface ResolvedButtonStyle {
  backgroundColor?: string;
  borderColor?: string;
  foregroundColor?: string;
  borderWidth: number;
}

@Component({
  selector: 'wr-buttons-widget',
  standalone: true,
  template: `
    <div
      class="wr-buttons-widget"
      [style.background]="background()"
      [style.border-radius.px]="cornerRadius()"
      [style.padding.px]="padding()"
    >
      @if (widget()?.buttons?.length) {
        <div class="w-full px-4 pt-3">
          @for (btn of widget()?.buttons ?? []; track btn.id) {
            @if (btn.url) {
              <a
                [href]="btn.url"
                target="_blank"
                rel="noopener noreferrer"
                class="block"
                style="text-decoration: none;"
              >
                <div
                  [id]="btn.id"
                  class="wr-button hover:opacity-80 duration-400 ease-in-out"
                  [style.min-height.px]="30"
                  [style.margin-bottom.px]="10"
                  [style.padding.px]="10"
                  [style.font-family]="plainFont()"
                  [style.background-color]="buttonBackground(btn)"
                  [style.color]="buttonForeground(btn)"
                  [style.border-radius.px]="cornerRadius()"
                  [style.border-width.px]="buttonBorderWidth(btn)"
                  [style.border-style]="'solid'"
                  [style.border-color]="buttonBorderColor(btn)"
                >
                  <span
                    style="padding: 1px; word-break: break-word;"
                    [style.color]="buttonForeground(btn)"
                  >
                    {{ btn.title }}
                  </span>
                </div>
              </a>
            } @else {
              <div
                [id]="btn.id"
                class="wr-button"
                [style.min-height.px]="30"
                [style.margin-bottom.px]="10"
                [style.padding.px]="10"
                [style.font-family]="plainFont()"
                [style.background-color]="buttonBackground(btn)"
                [style.color]="buttonForeground(btn)"
                [style.border-radius.px]="cornerRadius()"
                [style.border-width.px]="buttonBorderWidth(btn)"
                [style.border-style]="'solid'"
                [style.border-color]="buttonBorderColor(btn)"
              >
                <span
                  style="padding: 1px; word-break: break-word;"
                  [style.color]="buttonForeground(btn)"
                >
                  {{ btn.title }}
                </span>
              </div>
            }
          }
        </div>
      }
    </div>
  `,
})
export class ButtonsWidgetComponent {
  private widgetSig = signal<ButtonsWidget | null>(null);
  private themeSig = signal<Theme & { isDark?: boolean } | null>(null);

  @Input() set json(value: ButtonsWidget) {
    this.widgetSig.set(value);
  }

  @Input() set theme(value: Theme | undefined) {
    this.themeSig.set(value ?? null);
  }

  widget = computed(() => this.widgetSig());

  private isDark = computed(() => this.themeSig()?.isDark ?? false);

  private colors = computed(() => {
    const theme = this.themeSig();
    if (!theme) return null;
    return this.isDark() ? theme.darkColors : theme.lightColors;
  });

  background = computed(
    () =>
      this.widget()?.style?.['background'] ??
      'transparent',
  );

  padding = computed(
    () => (this.widget()?.style?.['padding'] as number | undefined) ?? 0,
  );

  cornerRadius = computed(
    () => this.widget()?.cornerRadius ?? 0,
  );

  plainFont = computed(
    () => this.themeSig()?.fonts?.plain?.name ?? 'inherit',
  );

  // COLORS

  private applyLightDark(
    base: ResolvedButtonStyle,
    light?: ButtonColorSet,
    dark?: ButtonColorSet,
  ): void {
    const isDark = this.isDark();

    if (!isDark) {
      if (light) {
        base.backgroundColor = light.background ?? base.backgroundColor;
        base.borderColor = light.border ?? base.borderColor;
        base.foregroundColor = light.primary ?? base.foregroundColor;
      }
    } else {
      if (dark) {
        base.backgroundColor =
          dark.background ??
          light?.background ??
          base.backgroundColor;

        base.borderColor =
          dark.border ??
          light?.border ??
          base.borderColor;

        base.foregroundColor =
          dark.primary ??
          light?.primary ??
          base.foregroundColor;
      } else if (light) {
        base.backgroundColor = light.background ?? base.backgroundColor;
        base.borderColor = light.border ?? base.borderColor;
        base.foregroundColor = light.primary ?? base.foregroundColor;
      }
    }
  }

  private resolveBaseStyle(style?: ButtonsStyle): ResolvedButtonStyle {
    const base: ResolvedButtonStyle = {
      backgroundColor: style?.backgroundColor,
      borderColor: style?.borderColor,
      foregroundColor: style?.foregroundColor ?? this.colors()?.primary,
      borderWidth: style?.borderWidth ?? 0,
    };

    if (style) {
      this.applyLightDark(base, style.lightColors, style.darkColors);
    }

    return base;
  }

  private resolveButtonStyle(btn: ButtonItem): ResolvedButtonStyle {
    const base = this.resolveBaseStyle(this.widget()?.buttonsStyle);

    // direct per-button overrides
    if (btn.customize) {
      if (btn.backgroundColor) base.backgroundColor = btn.backgroundColor;
      if (btn.borderColor) base.borderColor = btn.borderColor;
      if (btn.foregroundColor) base.foregroundColor = btn.foregroundColor;
      if (btn.borderWidth != null) base.borderWidth = btn.borderWidth;

      this.applyLightDark(base, btn.lightColors, btn.darkColors);
    }

    return {
      backgroundColor: base.backgroundColor,
      borderColor: base.borderColor,
      foregroundColor: base.foregroundColor,
      borderWidth: base.borderWidth ?? 0,
    };
  }

  // HELPERS

  buttonBackground(btn: ButtonItem): string {
    return this.resolveButtonStyle(btn).backgroundColor ?? 'transparent';
  }

  buttonBorderColor(btn: ButtonItem): string {
    return this.resolveButtonStyle(btn).borderColor ?? 'transparent';
  }

  buttonForeground(btn: ButtonItem): string {
    return (
      this.resolveButtonStyle(btn).foregroundColor ??
      this.colors()?.primary ??
      '#000'
    );
  }

  buttonBorderWidth(btn: ButtonItem): number {
    return this.resolveButtonStyle(btn).borderWidth ?? 0;
  }
}
